// pipeline-core.ts — apiFetch, redactSecrets, modeller, fil-helpers, discoverModels, withRetry, delt state.
// AUTO: del af pipeline (splittet fra pipeline.ts). Klassisk script — deler global scope. Indlæs FØR engines/phases/campaign.
/// <reference lib="dom" />
// ── Floor-Flow Pipeline ──────────────────────────────────────
// API config, model discovery, retry, KIE backup, Gemini calls

// ── Config ───────────────────────────────────────────────────
// API-nøgler ligger KUN på serveren. Alle Gemini-kald går gennem den auth-gated
// proxy /api/gemini/*, og OpenAI gennem /api/openai/image — ingen nøgle i browseren.
// apiFetch bruger authFetch (global i creator.html), som påsætter det Supabase-token
// /api/*-ruterne kræver; falder tilbage til plain fetch udenfor creator-siden.
function apiFetch(path: string, init?: RequestInit): Promise<Response> {
    const af = (window as any).authFetch as (typeof fetch) | undefined;
    return (af || fetch)(path, init);
}

// ── Sikkerhed: fjern API-nøgler/tokens fra tekst før den logges eller vises ──
// Dækker Google-nøgler (AIza…), OpenAI-nøgler (sk-…), ?key=-parametre,
// api_key:-felter i fejlsvar samt Bearer-tokens. Bruges på ALT der kan ende i
// en log, en konsol eller på skærmen.
function redactSecrets(input: unknown): string {
    let s = String(input ?? '');
    s = s.replace(/(api[_-]?key["']?\s*[:=]\s*["']?)[A-Za-z0-9_\-]+/gi, '$1[REDACTED]');
    s = s.replace(/([?&]key=)[^&\s"'#]+/gi, '$1[REDACTED]');
    s = s.replace(/AIza[0-9A-Za-z_\-]{35}/g, '[REDACTED_API_KEY]');
    s = s.replace(/sk-[A-Za-z0-9*_\-]{4,}/gi, '[REDACTED_API_KEY]');
    s = s.replace(/(Bearer\s+)[A-Za-z0-9_\-.]+/gi, '$1[REDACTED]');
    return s;
}

// Default primary engine when no per-call override is given. We keep this FALSE so
// Gemini is the default — and explicitly opt-IN to OpenAI per call (model + refine
// phases pass { preferOpenAi: true } below). Packshots stay on Gemini.
const USE_OPENAI_PRIMARY: boolean = false;
const GEMINI_BASE: string = '/api/gemini/models';

// Order = priority. Pro first for max quality; Flash variants as faster/cheaper
// fallbacks when Pro hits rate limits or isn't available on the key.
const IMAGE_MODELS: string[] = [
    'gemini-3-pro-image-preview',
    'gemini-3.1-flash-image-preview',
];
// The 2.x models are left (2026-10-26): gemini-2.5-flash was closed to new users this month,
// and the rest go the same way. 3.1 Pro first, 3.8 Flash when it is busy.
const TEXT_MODELS: string[] = [
    'gemini-3.1-pro-preview',
    'gemini-3.8-flash',
];

let imageModel: string = IMAGE_MODELS[0];
let textModel: string = TEXT_MODELS[0];

// ── Log context ─────────────────────────────────────────────
// 'packshot' | 'studio' — shared functions (geminiImage, withRetry, etc.)
// use this to route appendLog to the correct debug panel.
let __logContext: 'packshot' | 'studio' = 'packshot';

// ── Helpers ──────────────────────────────────────────────────

function getMime(file: File): string {
    return file.type || 'image/jpeg';
}

async function fileToBase64(file: File): Promise<string> {
    return new Promise((resolve) => {
        const r = new FileReader();
        r.onload = (e) => resolve((e.target!.result as string).split(',')[1]);
        r.readAsDataURL(file);
    });
}

async function base64ToFile(b64: string, mime: string, name: string): Promise<File> {
    const bytes = atob(b64);
    const arr = new Uint8Array(bytes.length);
    for (let i = 0; i < bytes.length; i++) arr[i] = bytes.charCodeAt(i);
    return new File([arr], name, { type: mime });
}

// ── Model discovery ──────────────────────────────────────────

interface GeminiModel {
    name: string;
    supportedGenerationMethods?: string[];
}

interface GeminiModelListResponse {
    models?: GeminiModel[];
}

async function discoverModels(): Promise<void> {
    appendLog('Finding available models...');
    try {
        const r = await apiFetch(GEMINI_BASE);
        const j: GeminiModelListResponse = await r.json();
        const names: string[] = (j.models || [])
            .filter((m) => m.supportedGenerationMethods?.includes('generateContent'))
            .map((m) => m.name.replace('models/', ''));

        for (const c of IMAGE_MODELS) { if (names.includes(c)) { imageModel = c; break; } }
        for (const c of TEXT_MODELS)  { if (names.includes(c)) { textModel  = c; break; } }

        appendLog(`Image model: ${imageModel}`);
        appendLog(`Text model: ${textModel}`);
    } catch (_e) {
        appendLog('Discovery failed — using defaults');
    }
}

// ── Retry logic ──────────────────────────────────────────────

// Module-scoped hook that generateLook sets so retry/fallback events
// from deep inside geminiImage/withRetry can surface on the active look card.
let __currentLookStageHook: ((msg: string) => void) | null = null;

function publishLookStage(msg: string): void {
    if (typeof __currentLookStageHook === 'function') {
        try { __currentLookStageHook(msg); } catch (_) { /* noop */ }
    }
}

async function withRetry<T>(fn: () => Promise<T>, label: string, maxAttempts: number = 5): Promise<T> {
    const delays: number[] = [5, 10, 20, 40, 60];
    for (let attempt = 1; attempt <= maxAttempts; attempt++) {
        try {
            return await fn();
        } catch (e) {
            const msg = (e as Error).message || String(e);
            // Vores egen proxy svarer 504 'upstream timed out' når Google tog >5 min.
            // At gentage samme model ville koste op til 5 min mere pr. forsøg —
            // kast i stedet straks, så kalderen falder videre til næste (hurtigere) model.
            const isProxyTimeout = msg.includes('upstream timed out');
            const is503 = msg.includes('503') || msg.includes('UNAVAILABLE');
            const is429 = msg.includes('429') || msg.includes('RESOURCE_EXHAUSTED');
            const is500 = msg.includes('500') || msg.includes('INTERNAL');
            const is502 = msg.includes('502');
            const is504 = msg.includes('504');
            if (!isProxyTimeout && (is503 || is429 || is500 || is502 || is504) && attempt < maxAttempts) {
                const wait = delays[attempt - 1] || 60;
                const reason = (is500 || is502 || is504) ? 'server error' : 'server busy';
                appendLog(`  ⏳ ${label} — ${reason}, waiting ${wait}s (attempt ${attempt}/${maxAttempts})...`);
                publishLookStage(`Retrying · ${reason} · ${attempt}/${maxAttempts}`);
                await new Promise((r) => setTimeout(r, wait * 1000));
            } else {
                throw e;
            }
        }
    }
    // TypeScript requires a return but this is unreachable
    throw new Error(`withRetry: exceeded ${maxAttempts} attempts`);
}

// ── Gemini: image with retry + KIE backup ───────────────────

